import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu as PrimeMenu } from "primereact/menu";
import { Button } from "primereact/button";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { getCurrentUser, logout } from "../../redux/slices/authSlice";
import { logMessage } from "../../utils/logMessage";
import { Avatar } from "primereact/avatar";
import { Skeleton } from "primereact/skeleton";
import { fetchBalance } from "../../redux/slices/accountSlice";
import { Toast } from "primereact/toast";
import { NotificationButton } from "../features/NotificationButton";
import { customerService } from "../../services/customerService";
import { Dialog } from "primereact/dialog";

interface NavBarProps {
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

const NavBar: React.FC<NavBarProps> = ({ isExpanded, setIsExpanded }) => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const toastRef = useRef<Toast>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const { currentUser, loading, customerMode } = useSelector((state: RootState) => state.auth);
  const { balance, isLoading } = useSelector((state: RootState) => state.account);

  const [showBalance, setShowBalance] = useState(false);
  const [requestingProduction, setRequestingProduction] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  const getTitle = () => {
    const titles: { [key: string]: string } = {
      "/dashboard/settings": "Settings",
      "/dashboard/settings/profile": "Profile",
      "/dashboard/settings/profile/change-password": "Manage Password",
      "/dashboard/services": "Services",
      "/dashboard/developers": "Developers",
      "/admin/partners": "Manage Partners",
      "/admin/withdrawals": "Manage Withdrawals",
      "/admin/customers": "Manage Customers",
      "/admin/users": "Manage users",
      "/admin/manage-document": "Manage Customers Documents",
      "/admin/manage-monetary-zone": "Manage Monetary Zones",
      "/admin/countries-monetary-zone": "Manage Countries",
      "/admin/manage-service": "Manage Services",
      "/admin/manage-ip-address": "Manage IP Address",
      "/admin/maintenance": "Maintenance",
      "/dashboard/history": "Transactions History",
      "/admin/transaction-history": "Transactions History",
      "/admin/manage-roles": "Manage Roles",
      "/dashboard/payment-link": "Manage Payment Link",
      "/admin/payment-link": "Payment Links List",
      "/admin/manage-withdrawals": "Manage withdrawals",
      "/dashboard/withdrawal-list": "withdrawal history",
      "/dashboard/manage-webhook": "Manage webhook",
    };
    return titles[location.pathname] || "Dashboard";
  };

  const openConfirmModal = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmRequestProduction = async () => {
    setRequestingProduction(true);
    setShowConfirmModal(false);
    try {
      const res = await customerService.requestToProduction();
      if (res.status === -1) {
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: res.message || "Failed to request production mode",
          life: 3000,
        });
        return;
      }

      toastRef.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Production request sent successfully",
        life: 3000,
      });

      // Refresh current user to update isRequestedToProduction status
      dispatch(getCurrentUser());
    } catch (error: any) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to request production mode",
        life: 3000,
      });
    } finally {
      setRequestingProduction(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout())
      .unwrap()
      .then(() => {
        navigate("/auth");
        localStorage.removeItem("otpConfirmed");
      })
      .catch((error) => {
        logMessage("Logout failed:", error);
      });
  };

  const handleProfileAction = (action: () => void) => {
    setIsProfileMenuOpen(false);
    action();
  };

  const profileItems = [
    { label: "Profile Details", icon: "pi pi-user", command: () => handleProfileAction(() => navigate("/dashboard/settings")) },
    { label: "Logout", icon: "pi pi-sign-out", command: () => handleProfileAction(handleLogout) },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!profileMenuRef.current) return;
      if (!profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isProfileMenuOpen]);

  useEffect(() => {
    if (currentUser?.role.role_code === "CUSTOMER"){
      dispatch(fetchBalance());
    }
  }, [dispatch]);

  const isCustomer = currentUser?.role.role_code === "CUSTOMER" || currentUser?.role.role_code === "ENTERPRISE";
  const isInProduction = customerMode === "PRODUCTION";

  const hasRequestedProduction = currentUser?.customer?.isRequestedToProduction;

  const confirmModalFooter = (
    <div className="flex justify-end gap-2">
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={() => setShowConfirmModal(false)}
        className="p-button-text"
        disabled={requestingProduction}
      />
      <Button
        label="Confirm Request"
        icon="pi pi-check"
        onClick={handleConfirmRequestProduction}
        loading={requestingProduction}
        className="bg-green-500 hover:bg-green-600 text-white"
        autoFocus
      />
    </div>
  );

  return (
    <header className="z-100 flex justify-between shadow-sm items-center bg-white p-7 py-4 h-20 border-b">

      <div className="flex items-center">
        <Button
          icon={isExpanded ? "pi pi-align-left" : "pi pi-align-right"}
          onClick={() => setIsExpanded(!isExpanded)}
          className="hidden md:block text-black p-0 border-none focus:outline-none focus:shadow-none"
        />

        <h1 className="hidden md:block text-md text-secondary-500">{getTitle()}</h1>

        {/* Affichage du mode actuel (lecture seule) */}
        {!loading && isCustomer && (
          <div className="p-4">
            <span className="text-secondary-500 text-sm font-medium">
              Mode: <span className={`font-bold ${customerMode === "PRODUCTION" ? "text-green-500" : "text-red-500"}`}>
                {customerMode === "PRODUCTION" ? "Production" : "Test"}
              </span>
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center space-x-4">
        {/* Go to Production Button - Only for customers not in production */}
        {!loading && isCustomer && !isInProduction && (
          <Button
            label={hasRequestedProduction ? "Request Pending" : "Go to Production"}
            icon={hasRequestedProduction ? "pi pi-clock" : "pi pi-arrow-right"}
            className={`text-sm ${
              hasRequestedProduction 
                ? "bg-gray-400 text-white cursor-not-allowed" 
                : "bg-green-500 hover:bg-green-600 text-white"
            }`}
            disabled={hasRequestedProduction || requestingProduction}
            loading={requestingProduction}
            onClick={openConfirmModal}
          />
        )}

        {!loading && isCustomer && (
          <div className="hidden md:flex items-center gap-2">
            <span className="text-secondary-500">Balance :</span>
            {isLoading ? (
              <Skeleton style={{ width: "100px", fontSize: "1rem" }} />
            ) : (
              <h5 className="cursor-pointer text-primary-500">
                {showBalance ? isNaN(Number(balance)) ? "0.00" : Number(balance).toFixed(2) : "******"}</h5>
            )}
            <span className="cursor-pointer text-primary-500" onClick={() => setShowBalance(!showBalance)}>
              {showBalance ? <i className="pi pi-eye-slash"></i> : <i className="pi pi-eye"></i>}
            </span>
          </div>
        )}
        <div className="hidden md:block">
         <NotificationButton/>
        </div>

        <div className="relative items-center" ref={profileMenuRef}>
          <Button
            label={`${currentUser?.firstname} ${currentUser?.lastname}`}
            icon="pi pi-angle-down"
            className="p-button-text text-secondary-500 flex items-center"
            onClick={(e) => {
              e.stopPropagation();
              setIsProfileMenuOpen((prev) => !prev);
            }}
          >
            <Avatar label={currentUser?.firstname?.[0]?.toUpperCase() || "V"} size="normal" className="text-secondary-500 w-8 h-8 ml-2" shape="circle" />
          </Button>

          {isProfileMenuOpen && (
            <div className="absolute right-0 mt-2 z-50">
              <PrimeMenu model={profileItems} />
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal for Go to Production */}
      <Dialog
        visible={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        header="Request Production Mode"
        footer={confirmModalFooter}
        style={{ width: '450px' }}
        closable={!requestingProduction}
      >
        <div className="py-4">
          <div className="flex items-start gap-3">
            <i className="pi pi-exclamation-triangle text-orange-500" style={{ fontSize: '2rem' }} />
            <div>
              <p className="mb-3 text-gray-700">
                Are you sure you want to request <strong>Production Mode</strong>?
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700 mb-2">
                  <i className="pi pi-info-circle mr-2"></i>
                  <strong>What happens next:</strong>
                </p>
                <ul className="text-sm text-blue-600 list-disc list-inside space-y-1">
                  <li>Your request will be reviewed by our admin team</li>
                  <li>You will be notified once your request is processed</li>
                  <li>Once approved, you can perform real transactions</li>
                </ul>
              </div>
              <p className="mt-3 text-sm text-gray-500">
                Please ensure all your KYC documents are verified before requesting production access.
              </p>
            </div>
          </div>
        </div>
      </Dialog>

      <Toast ref={toastRef} />
    </header>
  );
};

export default NavBar;

